import { useState } from 'react';
import { Phone, PhoneOff, PhoneCall, Clock, Activity, Users, ToggleLeft, ToggleRight } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/axios';
import { useCallStore } from '@/stores/callStore';
import { useRBAC } from '@/hooks/useRBAC';
import { formatCallDuration } from '@/utils/formatters';
import { Spinner } from '@/components/ui/Spinner';
import { Button } from '@/components/ui/Button';
import { SalesCallDashboard } from './SalesCallDashboard';
import toast from 'react-hot-toast';

interface Agent {
  id: string;
  name: string;
  isAvailable: boolean;
  city: string | null;
}

interface LiveCall {
  id: string;
  direction: string;
  status: string;
  durationSeconds: number;
  isLive: boolean;
  agent: { id: string; name: string } | null;
  startedAt: string | null;
}

export function LiveCallPage() {
  const { role, user } = useRBAC();
  if (role === 'sales_executive') return <SalesCallDashboard />;
  const { incomingCalls } = useCallStore();
  const [callTo, setCallTo] = useState('');
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['calls', 'live'],
    queryFn: async () => {
      const { data } = await apiClient.get<{ data: { liveData: LiveCall[]; agentAvailability: Agent[] } }>('/calls/live');
      return data.data;
    },
    refetchInterval: 5000,
  });

  const initiateCall = useMutation({
    mutationFn: async (phone: string) => {
      const { data } = await apiClient.post('/calls/initiate', { toPhone: phone });
      return data;
    },
    onSuccess: () => toast.success('Call initiated'),
    onError: () => toast.error('Failed to initiate call'),
  });

  const toggleAvailability = useMutation({
    mutationFn: async ({ agentId, isAvailable }: { agentId: string; isAvailable: boolean }) => {
      const { data } = await apiClient.patch(`/calls/agents/${agentId}/availability`, { isAvailable });
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['calls', 'live'] });
      toast.success('Availability updated');
    },
  });

  const isManager = role === 'super_admin' || role === 'category_manager' || role === 'team_lead';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Live Call Dashboard</h1>
          <p className="text-slate-500 text-sm mt-0.5">Real-time call monitoring and management</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="flex items-center gap-1.5 text-sm text-green-600 bg-green-50 px-3 py-1.5 rounded-lg border border-green-200">
            <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
            {data?.liveData?.length ?? 0} Active Calls
          </span>
        </div>
      </div>

      {/* Incoming Call Alerts */}
      {incomingCalls.length > 0 && (
        <div className="space-y-2">
          {incomingCalls.map(call => (
            <div key={call.callId} className={`flex items-center justify-between p-4 rounded-xl border-2 ${call.isRepeatCaller ? 'bg-blue-50 border-blue-200' : 'bg-green-50 border-green-300 animate-pulse'}`}>
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${call.isRepeatCaller ? 'bg-blue-500' : 'bg-green-500'}`}>
                  <Phone className="text-white fill-white" size={18} />
                </div>
                <div>
                  <div className="font-semibold text-slate-900">
                    {call.isRepeatCaller ? '🔁 Repeat Caller' : '📞 New Incoming Call'}
                  </div>
                  <div className="text-sm text-slate-500">
                    {call.isRepeatCaller ? 'Routing to assigned BD' : 'Routing to all available BDs'}
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                <Button variant="success" size="sm" icon={<Phone size={14} />}>Accept</Button>
                <Button variant="danger" size="sm" icon={<PhoneOff size={14} />}>Decline</Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Click-to-Call */}
      <div className="bg-brand-navy rounded-xl p-6 text-white">
        <h2 className="text-base font-semibold mb-3 flex items-center gap-2">
          <PhoneCall size={18} className="text-brand-cyan" />
          Click-to-Call
        </h2>
        <div className="flex gap-3">
          <input
            type="tel"
            value={callTo}
            onChange={e => setCallTo(e.target.value)}
            placeholder="+91 XXXXX XXXXX"
            className="flex-1 h-10 px-4 rounded-lg bg-white/10 border border-white/20 text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-brand-cyan/50 text-sm"
          />
          <Button
            variant="primary"
            icon={<Phone size={14} />}
            onClick={() => callTo && initiateCall.mutate(callTo)}
            loading={initiateCall.isPending}
            disabled={!callTo.trim()}
          >
            Call Now
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Active Calls */}
        <div className="bg-white rounded-xl shadow-card border border-slate-100">
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
            <h3 className="font-semibold text-slate-900 flex items-center gap-2">
              <Activity size={16} className="text-green-500" />
              Active Calls
            </h3>
            <span className="text-xs text-slate-400">{data?.liveData?.length ?? 0} calls</span>
          </div>
          {isLoading ? (
            <div className="flex items-center justify-center py-12"><Spinner /></div>
          ) : data?.liveData?.length === 0 ? (
            <div className="py-12 text-center text-slate-400 text-sm">
              <Phone size={32} className="mx-auto mb-2 opacity-30" />
              No active calls
            </div>
          ) : (
            <div className="divide-y divide-slate-50">
              {data?.liveData?.map(call => (
                <div key={call.id} className="px-5 py-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-2 h-2 rounded-full ${call.status === 'in_progress' ? 'bg-green-500 animate-pulse' : 'bg-yellow-400'}`} />
                    <div>
                      <div className="font-medium text-sm text-slate-900">{call.agent?.name ?? 'Unassigned'}</div>
                      <div className="text-xs text-slate-400 capitalize">{call.direction} • {call.status}</div>
                    </div>
                  </div>
                  <div className="text-sm font-mono text-slate-600 flex items-center gap-1">
                    <Clock size={12} />
                    {formatCallDuration(call.durationSeconds)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Agent Availability */}
        <div className="bg-white rounded-xl shadow-card border border-slate-100">
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
            <h3 className="font-semibold text-slate-900 flex items-center gap-2">
              <Users size={16} className="text-blue-500" />
              BD Availability
            </h3>
            <span className="text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
              {data?.agentAvailability?.filter(a => a.isAvailable).length ?? 0} available
            </span>
          </div>
          <div className="divide-y divide-slate-50">
            {data?.agentAvailability?.map(agent => (
              <div key={agent.id} className="px-5 py-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-2.5 h-2.5 rounded-full ${agent.isAvailable ? 'bg-green-500' : 'bg-red-400'}`} />
                  <div>
                    <div className="font-medium text-sm text-slate-900">{agent.name}</div>
                    {agent.city && <div className="text-xs text-slate-400">{agent.city}</div>}
                  </div>
                </div>
                {(isManager || user?.id === agent.id) && (
                  <button
                    onClick={() => toggleAvailability.mutate({ agentId: agent.id, isAvailable: !agent.isAvailable })}
                    className={`flex items-center gap-1.5 text-xs px-3 py-1 rounded-full font-medium transition-colors ${
                      agent.isAvailable
                        ? 'bg-green-50 text-green-700 hover:bg-green-100'
                        : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                    }`}
                  >
                    {agent.isAvailable ? <ToggleRight size={14} /> : <ToggleLeft size={14} />}
                    {agent.isAvailable ? 'Available' : 'Unavailable'}
                  </button>
                )}
              </div>
            ))}
            {!data?.agentAvailability?.length && (
              <div className="py-8 text-center text-slate-400 text-sm">No agents found</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
